import { useState } from 'react'
import { Search } from 'lucide-react'
import { MARKERS, MARKER_GROUPS } from './markers'
import { editorInput, editorLabel } from './editorUi'
import { useLanguage } from '@/contexts/LanguageContext'

/**
 * Simge seçici — sağ panelin "işaretçi" aracındaki gövdesi.
 *
 * İki şey değişti:
 *
 * - **Adlar basılır.** Simgeler yalnızca ikonla duruyordu ve hangisinin
 *   hangi şey olduğu ancak üstüne gelip beklemekle öğreniliyordu; ipucu metni
 *   bir liste değildir.
 * - **Arama var.** Kutuları gözle taramak, adı bilinen bir simge için
 *   en yavaş yol. Eşleşme İngilizce ad üzerinden yapılır çünkü katalog kod içi
 *   kimliktir (bkz. `markers.ts`).
 *
 * Bileşenin bir zamanlar iki kılığı vardı (`embedded` ve kendi kenar çubuğu);
 * ikincisi hiçbir yerden çizilmiyordu, o yüzden yalnızca gövde kaldı.
 */
export default function MarkersSidebar({ selectedMarkerId, onSelect }: {
  selectedMarkerId: string | null
  onSelect: (src: string, id: string) => void
}) {
  const { T } = useLanguage()
  const [query, setQuery] = useState('')

  const q = query.trim().toLowerCase()
  const shown = q ? MARKERS.filter(m => m.label.toLowerCase().includes(q)) : MARKERS

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 border-b border-white/10 p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/50" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={T.strategy.searchMarker}
            className={`${editorInput} pl-7`}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {/* Gruplar: taktik · sınıflar · araçlar · ikmal. Arama sonucu da gruplu
            kalır; boş kalan grup başlığıyla birlikte düşer. */}
        {MARKER_GROUPS.map(g => {
          const items = shown.filter(m => m.group === g)
          if (items.length === 0) return null
          return (
            <div key={g} className="mb-3 last:mb-0">
              <p className={`mb-2 ${editorLabel}`}>{T.strategy.markerGroups[g]}</p>
              <div className="grid grid-cols-4 gap-1.5">
                {items.map(m => (
                  <button
                    key={m.id}
                    onClick={() => onSelect(m.src, m.id)}
                    title={m.label}
                    className={`flex cursor-pointer flex-col items-center gap-1 rounded-sm border px-1 py-1.5 transition-colors ${
                      selectedMarkerId === m.id
                        ? 'border-primary/60 bg-primary/15'
                        : 'border-white/10 bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <img src={m.src} alt="" aria-hidden className="h-6 w-6" />
                    {/* Ad oyunun terimi — çevrilmez, büyütülmez. */}
                    <span className="w-full truncate text-center text-[9px] leading-tight text-muted-foreground">
                      {m.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )
        })}
        {shown.length === 0 && (
          <p className="pt-2 text-xs text-muted-foreground/60">{T.strategy.library.noSearchResult}</p>
        )}
      </div>
    </div>
  )
}
